import {
  InformationCircleIcon,
  ChartBarIcon,
  CogIcon,
} from '@heroicons/react/outline'
import { useState, useEffect, useCallback, useRef } from 'react'
import { Grid } from './components/grid/Grid'
import { Keyboard } from './components/keyboard/Keyboard'
import { InfoModal } from './components/modals/InfoModal'
import { StatsModal } from './components/modals/StatsModal'
import { SettingsModal } from './components/modals/SettingsModal'
import {
  GAME_TITLE,
  WIN_MESSAGES,
  GAME_COPIED_MESSAGE,
  NOT_ENOUGH_LETTERS_MESSAGE,
  WORD_NOT_FOUND_MESSAGE,
  CORRECT_WORD_MESSAGE,
  HARD_MODE_ALERT_MESSAGE,
} from './constants/strings'
import {
  MAX_WORD_LENGTH,
  MAX_CHALLENGES,
  REVEAL_TIME_MS,
  GAME_LOST_INFO_DELAY,
  WELCOME_INFO_MODAL_MS,
} from './constants/settings'
import {
  isWordInWordList,
  isWinningWord,
  findFirstUnusedReveal,
  unicodeLength,
  firstGameDateKey,
  getGameDay,
  getTodayDateKey,
  normalizeDateKey,
} from './lib/words'
import { addStatsForCompletedGame, loadStats } from './lib/stats'
import {
  loadGameStateFromLocalStorage,
  saveGameStateToLocalStorage,
  saveGameStateForDateToLocalStorage,
  setStoredIsHighContrastMode,
  getStoredIsHighContrastMode,
  getGateDefinitionsMode,
  setGateDefinitionsMode,
} from './lib/localStorage'
import { default as GraphemeSplitter } from 'grapheme-splitter'

import './App.css'
import { AlertContainer } from './components/alerts/AlertContainer'
import { useAlert } from './context/AlertContext'
import {
  trackFirstInteraction,
  trackFirstSuccessfulTurn,
  trackGameFinish,
} from './lib/analytics'

const getInitialDateKey = () => {
  const dateKey = new URLSearchParams(window.location.search).get('date')
  return normalizeDateKey(dateKey || getTodayDateKey())
}

const updateDateUrl = (dateKey: string, isToday: boolean) => {
  const url = new URL(window.location.href)

  if (isToday) {
    url.searchParams.delete('date')
  } else {
    url.searchParams.set('date', dateKey)
  }

  window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`)
}

function App() {
  const prefersDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches

  const { showError: showErrorAlert, showSuccess: showSuccessAlert } =
    useAlert()
  const [selectedDateKey, setSelectedDateKey] = useState(getInitialDateKey)
  const gameDay = getGameDay(selectedDateKey)
  const todayDateKey = getTodayDateKey()
  const isTodayGame = gameDay.dateKey === todayDateKey
  const hasScheduledWelcomeModal = useRef(false)
  const [currentGuess, setCurrentGuess] = useState('')
  const [isGameWon, setIsGameWon] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false)
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
  const [currentRowClass, setCurrentRowClass] = useState('')
  const [isGameLost, setIsGameLost] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('theme')
      ? localStorage.getItem('theme') === 'dark'
      : prefersDarkMode
      ? true
      : false
  )
  const [isHighContrastMode, setIsHighContrastMode] = useState(
    getStoredIsHighContrastMode()
  )
  const [isGateDefinitionsMode, setIsGateDefinitionsMode] = useState(
    getGateDefinitionsMode()
  )
  const [isRevealing, setIsRevealing] = useState(false)
  const [guesses, setGuesses] = useState<string[]>(() => {
    const includeLegacy = selectedDateKey === getTodayDateKey()
    const loaded = loadGameStateFromLocalStorage(selectedDateKey, includeLegacy)
    if (loaded?.solution !== gameDay.solution) {
      return []
    }
    return loaded.guesses
  })

  const [stats, setStats] = useState(() => loadStats())

  const [isHardMode, setIsHardMode] = useState(
    localStorage.getItem('gameMode')
      ? localStorage.getItem('gameMode') === 'hard'
      : false
  )

  // Analytics tracking states
  const [hasTrackedFirstInteraction, setHasTrackedFirstInteraction] =
    useState(false)
  const [hasTrackedFirstSuccessfulTurn, setHasTrackedFirstSuccessfulTurn] =
    useState(false)

  const loadGuessesForDate = useCallback(
    (dateKey: string, solution: string) => {
      const includeLegacy = dateKey === getTodayDateKey()
      const loaded = loadGameStateFromLocalStorage(dateKey, includeLegacy)
      return loaded?.solution === solution ? loaded.guesses : []
    },
    []
  )

  const loadGameForDate = useCallback(
    (dateKey: string, shouldUpdateUrl: boolean) => {
      const nextGameDay = getGameDay(dateKey)
      const nextTodayDateKey = getTodayDateKey()
      const nextGuesses = loadGuessesForDate(
        nextGameDay.dateKey,
        nextGameDay.solution
      )
      const gameWasWon = nextGuesses.includes(nextGameDay.solution)

      setSelectedDateKey(nextGameDay.dateKey)
      setGuesses(nextGuesses)
      setCurrentGuess('')
      setCurrentRowClass('')
      setIsRevealing(false)
      setIsGameWon(gameWasWon)
      setIsGameLost(nextGuesses.length === MAX_CHALLENGES && !gameWasWon)
      setHasTrackedFirstInteraction(false)
      setHasTrackedFirstSuccessfulTurn(false)

      if (shouldUpdateUrl) {
        updateDateUrl(
          nextGameDay.dateKey,
          nextGameDay.dateKey === nextTodayDateKey
        )
      }
    },
    [loadGuessesForDate]
  )

  useEffect(() => {
    if (hasScheduledWelcomeModal.current) {
      return
    }

    // if no game state on load,
    // show the user the how-to info modal
    if (
      !loadGameStateFromLocalStorage(
        selectedDateKey,
        selectedDateKey === getTodayDateKey()
      )
    ) {
      setTimeout(() => {
        setIsInfoModalOpen(true)
      }, WELCOME_INFO_MODAL_MS)
      hasScheduledWelcomeModal.current = true
    }
  }, [selectedDateKey])

  useEffect(() => {
    const gameWasWon = guesses.includes(gameDay.solution)
    setIsGameWon(gameWasWon)
    setIsGameLost(guesses.length === MAX_CHALLENGES && !gameWasWon)
  }, [gameDay.solution, guesses])

  useEffect(() => {
    const onPopState = () => {
      loadGameForDate(getInitialDateKey(), false)
    }

    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [loadGameForDate])

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    if (isHighContrastMode) {
      document.documentElement.classList.add('high-contrast')
    } else {
      document.documentElement.classList.remove('high-contrast')
    }
  }, [isDarkMode, isHighContrastMode])

  const handleDarkMode = (isDark: boolean) => {
    setIsDarkMode(isDark)
    localStorage.setItem('theme', isDark ? 'dark' : 'light')
  }

  const handleHardMode = (isHard: boolean) => {
    if (guesses.length === 0 || localStorage.getItem('gameMode') === 'hard') {
      setIsHardMode(isHard)
      localStorage.setItem('gameMode', isHard ? 'hard' : 'normal')
    } else {
      showErrorAlert(HARD_MODE_ALERT_MESSAGE)
    }
  }

  const handleHighContrastMode = (isHighContrast: boolean) => {
    setIsHighContrastMode(isHighContrast)
    setStoredIsHighContrastMode(isHighContrast)
  }

  const handleGateDefinitionsMode = (gateDefintions: boolean) => {
    setGateDefinitionsMode(gateDefintions)
    setIsGateDefinitionsMode(gateDefintions)
  }

  const handleDateChange = (dateKey: string) => {
    loadGameForDate(dateKey, true)
  }

  const clearCurrentRowClass = () => {
    setCurrentRowClass('')
  }

  useEffect(() => {
    const gameState = { guesses, solution: gameDay.solution }
    saveGameStateForDateToLocalStorage(gameDay.dateKey, gameState)

    if (isTodayGame) {
      saveGameStateToLocalStorage(gameState)
    }
  }, [guesses, gameDay.dateKey, gameDay.solution, isTodayGame])

  useEffect(() => {
    if (isGameWon) {
      const winMessage =
        WIN_MESSAGES[Math.floor(Math.random() * WIN_MESSAGES.length)]
      const delayMs = REVEAL_TIME_MS * MAX_WORD_LENGTH

      showSuccessAlert(winMessage, {
        delayMs,
        onClose: () => setIsStatsModalOpen(true),
      })
    }

    if (isGameLost) {
      setTimeout(() => {
        setIsStatsModalOpen(true)
      }, GAME_LOST_INFO_DELAY)
    }
  }, [isGameWon, isGameLost, showSuccessAlert])

  const onChar = (value: string) => {
    if (
      unicodeLength(`${currentGuess}${value}`) <= MAX_WORD_LENGTH &&
      guesses.length < MAX_CHALLENGES &&
      !isGameWon
    ) {
      setCurrentGuess(`${currentGuess}${value}`)

      // Track first interaction with the game
      if (!hasTrackedFirstInteraction && currentGuess.length === 0) {
        trackFirstInteraction()
        setHasTrackedFirstInteraction(true)
      }
    }
  }

  const onDelete = () => {
    setCurrentGuess(
      new GraphemeSplitter().splitGraphemes(currentGuess).slice(0, -1).join('')
    )
  }

  const onEnter = () => {
    if (isGameWon || isGameLost) {
      return
    }

    if (!(unicodeLength(currentGuess) === MAX_WORD_LENGTH)) {
      setCurrentRowClass('jiggle')
      return showErrorAlert(NOT_ENOUGH_LETTERS_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }

    if (!isWordInWordList(currentGuess)) {
      setCurrentRowClass('jiggle')
      return showErrorAlert(WORD_NOT_FOUND_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }

    // enforce hard mode - all guesses must contain all previously revealed letters
    if (isHardMode) {
      const firstMissingReveal = findFirstUnusedReveal(
        currentGuess,
        guesses,
        gameDay.solution
      )
      if (firstMissingReveal) {
        setCurrentRowClass('jiggle')
        return showErrorAlert(firstMissingReveal, {
          onClose: clearCurrentRowClass,
        })
      }
    }

    setIsRevealing(true)
    // turn this back off after all
    // chars have been revealed
    setTimeout(() => {
      setIsRevealing(false)
    }, REVEAL_TIME_MS * MAX_WORD_LENGTH)

    const winningWord = isWinningWord(currentGuess, gameDay.solution)

    if (
      unicodeLength(currentGuess) === MAX_WORD_LENGTH &&
      guesses.length < MAX_CHALLENGES &&
      !isGameWon
    ) {
      setGuesses([...guesses, currentGuess])
      setCurrentGuess('')

      // Track first successful turn (valid word entered)
      if (!hasTrackedFirstSuccessfulTurn) {
        trackFirstSuccessfulTurn(guesses.length + 1)
        setHasTrackedFirstSuccessfulTurn(true)
      }

      if (winningWord) {
        if (isTodayGame) {
          setStats(addStatsForCompletedGame(stats, guesses.length))
        }
        // Track game won
        trackGameFinish('win', guesses.length + 1)
        return setIsGameWon(true)
      }

      if (guesses.length === MAX_CHALLENGES - 1) {
        if (isTodayGame) {
          setStats(addStatsForCompletedGame(stats, guesses.length + 1))
        }
        setIsGameLost(true)
        // Track game lost
        trackGameFinish('loss', guesses.length + 1)
        showErrorAlert(CORRECT_WORD_MESSAGE(gameDay.solution), {
          persist: true,
          delayMs: REVEAL_TIME_MS * MAX_WORD_LENGTH + 1,
        })
      }
    }
  }

  return (
    <div className="pt-2 pb-8 max-w-7xl mx-auto sm:px-6 lg:px-8">
      <div className="flex w-80 mx-auto items-center mb-8 mt-20">
        <h1 className="text-xl ml-2.5 grow font-bold dark:text-white">
          {GAME_TITLE}
        </h1>
        <InformationCircleIcon
          className="h-6 w-6 mr-2 cursor-pointer dark:stroke-white"
          onClick={() => setIsInfoModalOpen(true)}
        />
        <ChartBarIcon
          className="h-6 w-6 mr-3 cursor-pointer dark:stroke-white"
          onClick={() => setIsStatsModalOpen(true)}
        />
        <CogIcon
          className="h-6 w-6 mr-3 cursor-pointer dark:stroke-white"
          onClick={() => setIsSettingsModalOpen(true)}
        />
      </div>
      <Grid
        guesses={guesses}
        currentGuess={currentGuess}
        solution={gameDay.solution}
        isRevealing={isRevealing}
        currentRowClassName={currentRowClass}
      />
      <Keyboard
        onChar={onChar}
        onDelete={onDelete}
        onEnter={onEnter}
        guesses={guesses}
        solution={gameDay.solution}
        isRevealing={isRevealing}
        showAnnotations={isGateDefinitionsMode}
      />
      <div className="w-80 mx-auto mt-5 mb-3">
        <input
          aria-label="Puzzle date"
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-slate-800 dark:text-white"
          type="date"
          min={firstGameDateKey}
          max={todayDateKey}
          value={gameDay.dateKey}
          onChange={(event) => handleDateChange(event.target.value)}
        />
      </div>
      <div className="attribution-container">
        <div className="attribution-text">
          Created by
          <br />
          Iftach Yakar
        </div>
        <div className="attribution-links">
          <a
            href="https://x.com/QuantumYakar"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="X (Twitter) profile"
            className="attribution-link"
          >
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 512 512"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z"></path>
            </svg>
          </a>
          <a
            href="https://bsky.app/profile/quantumyakar.bsky.social"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Bluesky profile"
            className="attribution-link"
          >
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              role="img"
              viewBox="0 0 24 24"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z"></path>
            </svg>
          </a>
          <a
            href="https://github.com/DeDuckProject"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="GitHub profile"
            className="attribution-link"
          >
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 496 512"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M165.9 397.4c0 2-2.3 3.6-5.2 3.6-3.3.3-5.6-1.3-5.6-3.6 0-2 2.3-3.6 5.2-3.6 3-.3 5.6 1.3 5.6 3.6zm-31.1-4.5c-.7 2 1.3 4.3 4.3 4.9 2.6 1 5.6 0 6.2-2s-1.3-4.3-4.3-5.2c-2.6-.7-5.5.3-6.2 2.3zm44.2-1.7c-2.9.7-4.9 2.6-4.6 4.9.3 2 2.9 3.3 5.9 2.6 2.9-.7 4.9-2.6 4.6-4.6-.3-1.9-3-3.2-5.9-2.9zM244.8 8C106.1 8 0 113.3 0 252c0 110.9 69.8 205.8 169.5 239.2 12.8 2.3 17.3-5.6 17.3-12.1 0-6.2-.3-40.4-.3-61.4 0 0-70 15-84.7-29.8 0 0-11.4-29.1-27.8-36.6 0 0-22.9-15.7 1.6-15.4 0 0 24.9 2 38.6 25.8 21.9 38.6 58.6 27.5 72.9 20.9 2.3-16 8.8-27.1 16-33.7-55.9-6.2-112.3-14.3-112.3-110.5 0-27.5 7.6-41.3 23.6-58.9-2.6-6.5-11.1-33.3 2.6-67.9 20.9-6.5 69 27 69 27 20-5.6 41.5-8.5 62.8-8.5s42.8 2.9 62.8 8.5c0 0 48.1-33.6 69-27 13.7 34.7 5.2 61.4 2.6 67.9 16 17.7 25.8 31.5 25.8 58.9 0 96.5-58.9 104.2-114.8 110.5 9.2 7.9 17 22.9 17 46.4 0 33.7-.3 75.4-.3 83.6 0 6.5 4.6 14.4 17.3 12.1C428.2 457.8 496 362.9 496 252 496 113.3 383.5 8 244.8 8zM97.2 352.9c-1.3 1-1 3.3.7 5.2 1.6 1.6 3.9 2.3 5.2 1 1.3-1 1-3.3-.7-5.2-1.6-1.6-3.9-2.3-5.2-1zm-10.8-8.1c-.7 1.3.3 2.9 2.3 3.9 1.6 1 3.6.7 4.3-.7.7-1.3-.3-2.9-2.3-3.9-2-.6-3.6-.3-4.3.7zm32.4 35.6c-1.6 1.3-1 4.3 1.3 6.2 2.3 2.3 5.2 2.6 6.5 1 1.3-1.3.7-4.3-1.3-6.2-2.2-2.3-5.2-2.6-6.5-1zm-11.4-14.7c-1.6 1-1.6 3.6 0 5.9 1.6 2.3 4.3 3.3 5.6 2.3 1.6-1.3 1.6-3.9 0-6.2-1.4-2.3-4-3.3-5.6-2z"></path>
            </svg>
          </a>
          <a
            href="https://www.linkedin.com/in/iftach-yakar-1511b344/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn profile"
            className="attribution-link"
          >
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 448 512"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path>
            </svg>
          </a>
        </div>
      </div>
      <InfoModal
        isOpen={isInfoModalOpen}
        handleClose={() => setIsInfoModalOpen(false)}
      />
      <StatsModal
        isOpen={isStatsModalOpen}
        handleClose={() => setIsStatsModalOpen(false)}
        guesses={guesses}
        gameStats={stats}
        isGameLost={isGameLost}
        isGameWon={isGameWon}
        solution={gameDay.solution}
        solutionIndex={gameDay.solutionIndex}
        dateKey={gameDay.dateKey}
        nextDay={gameDay.nextDay}
        isToday={isTodayGame}
        handleShare={() => showSuccessAlert(GAME_COPIED_MESSAGE)}
        isHardMode={isHardMode}
        isDarkMode={isDarkMode}
        isHighContrastMode={isHighContrastMode}
      />
      <SettingsModal
        isOpen={isSettingsModalOpen}
        handleClose={() => setIsSettingsModalOpen(false)}
        isHardMode={isHardMode}
        handleHardMode={handleHardMode}
        isDarkMode={isDarkMode}
        handleDarkMode={handleDarkMode}
        isHighContrastMode={isHighContrastMode}
        handleHighContrastMode={handleHighContrastMode}
        isGateDefinitionsMode={isGateDefinitionsMode}
        handleGateDefinitionsMode={handleGateDefinitionsMode}
      />

      <AlertContainer />
    </div>
  )
}

export default App
